import React from 'react';
import { FileText, Trash2 } from 'lucide-react';
import FundingDocumentTypeSelector from './FundingDocumentTypeSelector';
import { OTHER_DOCUMENT_TYPE, formatFileSize } from './fundingDocumentTypes';

const STATUS_LABELS = {
  ready: 'Ready',
  scanning: 'Scanning',
  extracted: 'Extracted',
  extraction_unavailable: 'Extraction unavailable',
  needs_review: 'Needs review',
  error: 'Error',
  converting: 'Converting iPhone photo…',
  converted: 'Converted from HEIC',
};

const FundingUploadedDocumentCard = ({ document, onChange, onRemove }) => {
  const statusClass = `fg-doc-card__status fg-doc-card__status--${document.scanStatus || 'ready'}`;
  const showCustomName = document.documentTypes?.includes(OTHER_DOCUMENT_TYPE);
  const conversionLabel = document.conversionStatus === 'converted'
    || document.convertedFromHeic
    ? 'Converted from HEIC'
    : document.conversionStatus === 'converting'
      ? 'Converting iPhone photo…'
      : document.conversionStatus === 'failed'
        ? document.conversionError || 'HEIC conversion failed'
        : null;

  const patch = (updates) => onChange?.({ ...document, ...updates });

  return (
    <article className="fg-doc-card" data-testid={`upload-doc-card-${document.id}`}>
      <div className="fg-doc-card__header">
        <div className="fg-doc-card__title">
          {document.previewUrl ? (
            <img
              src={document.previewUrl}
              alt=""
              className="fg-doc-card__thumb"
              data-testid={`upload-doc-preview-${document.id}`}
            />
          ) : (
            <FileText className="size-5 text-[var(--fg-accent)]" />
          )}
          <div>
            <strong>{document.fileName}</strong>
            <span className={statusClass}>
              {STATUS_LABELS[document.scanStatus] || 'Ready'}
              {document.fileSize ? ` · ${formatFileSize(document.fileSize)}` : ''}
            </span>
            {conversionLabel ? (
              <span className="fg-doc-card__conversion" data-testid={`upload-doc-conversion-${document.id}`}>
                {conversionLabel}
                {document.originalFilename && document.convertedFromHeic
                  ? ` · original ${document.originalFilename}`
                  : ''}
              </span>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          className="fg-inline-icon-btn"
          aria-label={`Remove ${document.fileName}`}
          onClick={() => onRemove?.(document.id)}
        >
          <Trash2 className="size-4" />
        </button>
      </div>

      <FundingDocumentTypeSelector
        id={`doc-types-${document.id}`}
        value={document.documentTypes}
        onChange={(documentTypes) => patch({
          documentTypes,
          primaryDocumentType: documentTypes[0] || '',
        })}
      />

      {showCustomName ? (
        <div className="funding-client-field fg-form-field">
          <label htmlFor={`custom-name-${document.id}`}>Name this document</label>
          <input
            id={`custom-name-${document.id}`}
            value={document.customName}
            onChange={(e) => patch({ customName: e.target.value })}
            placeholder="Example: Merchant processing statement, lease agreement, lender preapproval"
            data-testid={`upload-doc-custom-name-${document.id}`}
          />
        </div>
      ) : null}

      <div className="fg-doc-card__options">
        <label className="fg-checkbox-row">
          <input
            type="checkbox"
            checked={Boolean(document.useForExtraction)}
            onChange={(e) => patch({ useForExtraction: e.target.checked })}
          />
          <span>Use for extraction</span>
        </label>
        <label className="fg-checkbox-row">
          <input
            type="checkbox"
            checked={Boolean(document.saveToClientProfile)}
            onChange={(e) => patch({ saveToClientProfile: e.target.checked })}
          />
          <span>Save to client profile</span>
        </label>
        <label className="fg-checkbox-row">
          <input
            type="checkbox"
            checked={Boolean(document.attachToApplication)}
            onChange={(e) => patch({ attachToApplication: e.target.checked })}
          />
          <span>Attach to application</span>
        </label>
      </div>
    </article>
  );
};

export default FundingUploadedDocumentCard;
